import axios from 'axios';
import { remove, sum } from 'lodash';
import PQueue from 'p-queue';
import { createHttpClient } from './createHttpClient';
import { getUploadParts } from './getUploadParts';
import type { Part, UploaderOptions, UploaderOptionsInput } from './types';
import { UploadProgress } from './UploadProgress';
import { withErrorBoundary } from './utils/withErrorBoundary';

export const createQueue = (options?: { concurrency: number }) =>
  new PQueue(options);

const awsMinPartSize = 5 * 1024 * 1024; // AWS multipart min file size

// In the first 8m+, the exponential backoff will trigger 8 attempts. In the
// following 23-point-something hours, it's an attempt every 5 mins:
//
// 8 + (24 * 60 / 5) = 296
//
// So rounding it up to a nice 300 which will put it over 24 hours for sure.
const maxRetryAttempts = 300;

export class Uploader {
  file!: File;

  _isPaused = true;

  private hasStarted = false;

  private uploadUrls!: string[];

  private totalPartCount!: number;

  private options: Omit<UploaderOptions, 'queue'> & {
    queue: NonNullable<UploaderOptions['queue']>;
  };

  private prevRatio: number | undefined;

  private prevSample:
    | {
        timestamp: number;
        bytesSent: number;
        bytesPerSecond: number;
      }
    | undefined;

  get prevSampleData() {
    return this.prevSample;
  }

  // Parts that are queued or uploading. This is mutable.
  private pendingParts: Part[] = [];

  get pendingPartsList(): Part[] {
    return this.pendingParts;
  }

  // Parts that have finished uploading
  private completedParts: Part[] = [];

  get completedPartsList(): Part[] {
    return this.completedParts;
  }

  constructor(
    file: File,
    totalPartCount: number,
    uploadUrls: string[],
    options: UploaderOptionsInput
  ) {
    this.options = {
      queue: createQueue({ concurrency: 4 }) ?? options.queue,
      meta: options.meta ?? {},
      minPartSize: options.minPartSize ?? awsMinPartSize,
      headers: options.headers,
      callbacks: Object.fromEntries(
        Object.entries(options.callbacks ?? {})
          .filter(([, callback]) => callback)
          .map(([key, callback]) => [key, withErrorBoundary(callback)])
      ),
    };

    Object.assign(this, {
      file,
      uploadUrls,
      totalPartCount,
    });

    return this;
  }

  // Expose createHttpClient as instance function for tests
  // eslint-disable-next-line class-methods-use-this
  private createHttpClient({
    retries,
    onRetry,
  }: Parameters<typeof createHttpClient>[0]) {
    return createHttpClient({
      retries,
      onRetry,
      headers: this.options.headers,
    });
  }

  private initSampling() {
    this.prevSample = {
      timestamp: performance.now(),
      bytesSent: 0,
      bytesPerSecond: 0,
    };
  }

  private reportPartComplete(part: Part): void {
    // Mark this part as uploaded
    this.completedParts.push(part);
    remove(this.pendingParts, (p) => p.index === part.index);

    // When every part is uploaded, emit event
    if (this.completedParts.length === this.totalPartCount) {
      this.options.callbacks?.onSuccess?.();
    }
  }

  private setPartProgress(
    partIndex: number,
    event: Pick<ProgressEvent, 'loaded'>
  ): void {
    // Update sent bytes for the part
    const pendingPart = this.pendingParts.find((p) => p.index === partIndex);

    if (pendingPart) pendingPart.bytesSent = event.loaded;

    const bytesTotal = this.file.size;
    const bytesSent = sum([
      ...this.pendingParts.map((part) => part.bytesSent),
      ...this.completedParts.map((part) => part.blob.size),
    ]);
    const ratio = bytesSent / bytesTotal;

    if (this.prevRatio && ratio - this.prevRatio < 0.01 && ratio !== 1) return;
    this.prevRatio = ratio;

    // Estimate upload speed.
    //
    // This is sampled at most once per `MIN_SAMPLE_INTERVAL` to even out
    // fluctuations that happen when parts report progress too close to each
    // other.
    if (!this.prevSample) return;
    const now = performance.now();
    const MIN_SAMPLE_INTERVAL = 2000;
    const elapsedTime = now - this.prevSample.timestamp;
    let { bytesPerSecond } = this.prevSample;
    if (!bytesPerSecond || elapsedTime > MIN_SAMPLE_INTERVAL) {
      bytesPerSecond = Math.round(
        (bytesSent - this.prevSample.bytesSent) / (elapsedTime / 1000)
      );

      // Save current state for next update
      this.prevSample = {
        ...this.prevSample,
        timestamp: performance.now(),
        bytesSent,
        bytesPerSecond,
      };
    }

    const uploadProgress = new UploadProgress({
      bytesSent,
      bytesTotal,
      bytesPerSecond,
    });

    if (!this.isPaused()) {
      if (!this.hasStarted) {
        this.hasStarted = true;
        this.options.callbacks?.onStart?.();
      }
      // Report progress
      this.options.callbacks?.onProgress?.(uploadProgress.values);
    }
  }

  private queueUploads(parts: Part[]): void {
    const { queue } = this.options;

    this._isPaused = false;

    // Initialize upload speed sampling when upload (re)starts
    this.initSampling();

    this.pendingParts = parts.map((part) => {
      const { url, blob } = part;
      const cancelSource = axios.CancelToken.source();
      const { cancel } = cancelSource;

      // Queue upload request
      const job = () => {
        // Create a new client every time just so that `onRetry` can be correlated to
        // the part's request.
        return this.createHttpClient({
          // Keep retrying until S3 upload url expires in 24 hours, at which point a
          // 403 error will be returned. With a 403 error, axios-retry will stop
          // retrying and emit the "error" event below.
          retries: maxRetryAttempts,
          onRetry: (retryCount) => {
            // eslint-disable-next-line no-param-reassign
            part.retryCount = retryCount;
          },
        })
          .put(url, blob, {
            cancelToken: cancelSource.token,
            headers: { 'content-type': blob.type },
            timeout: 0,
            onUploadProgress: (event) =>
              this.setPartProgress(part.index, event),
          })
          .then(() => this.reportPartComplete(part))
          .catch((error) => {
            // If the request errors out after retries (not manually canceled),
            // stop uploading all the other parts
            const isManuallyCanceled = axios.isCancel(error);
            if (!isManuallyCanceled) {
              this.pause();
              this.options.callbacks?.onError?.(error);
            }
          });
      };

      const req = queue.add(job);

      return {
        ...part,
        bytesSent: 0,

        // Expose a handler to abort the HTTP request
        cancel,

        // Unit tests need to get/set the request state of individual parts
        req: process.env.NODE_ENV === 'test' ? req : undefined,
      };
    });
  }

  private abortPendingParts(): void {
    this.pendingParts = (
      this.pendingParts as Array<Part & { cancel: () => void }>
    ).map((part) => {
      // Aborting the request will also remove the part from the queue by
      // rejecting the promise
      if (typeof part.cancel === 'function') part.cancel();

      return {
        ...part,
        cancel: undefined,
        bytesSent: 0,
      };
    });
  }

  /**
   * Start or resume uploading the file.
   */
  upload = () => {
    if (this.pendingParts.length) {
      this.resume();
    } else {
      this.start();
    }
    return this;
  };

  /**
   * Stop uploading data.
   */
  pause = (): boolean => {
    if (this._isPaused) return false;

    this._isPaused = true;
    this.abortPendingParts();

    return true;
  };

  cancel = () => this.pause();

  /**
   * Split up file into parts and queue the parts to be uploaded.
   * @returns {Uploader} - This instance.
   */
  private start = () => {
    const { meta, minPartSize } = this.options;

    // Split files into parts and attach meta object to them
    const allParts = getUploadParts(
      this.file,
      this.uploadUrls,
      minPartSize
    ).map((part) => ({ ...part, meta }));

    this.queueUploads(allParts);
  };

  /**
   * Resume uploading parts that have not been completely uploaded.
   */
  private resume = () => {
    if (!this._isPaused) return;

    const incompleteParts = this.pendingParts.filter(
      (part) => part.bytesSent < part.blob.size
    );
    this.queueUploads(incompleteParts);
  };

  public isPaused() {
    return this._isPaused;
  }
}
